import {
  Box,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  Hidden,
  TableBody,
  useTheme,
  makeStyles,
  Theme,
  useMediaQuery,
  IconButton
} from "@material-ui/core";
import { EProductType, Payment } from "../../../api/Types";
import { QuoteDeduction } from "../../../api/Quote/QuoteDeduction";
import { QuoteLineItem } from "../../../api/Quote/QuoteLineItem";
import { Quote } from "../../../api/Quote/Quote";
import { Link as RouterLink } from "react-router-dom";
import {
  allowCustomerEdits,
  deductionSort,
  quoteDeductionValue,
  hasAddons,
  lineAddonSort,
  lineItemSort,
  quoteBalanceOutstanding,
  // quotePaymentsMade,
  quoteVat
} from "../../../app/utils";
import { colors } from "swyft-react-core";
import { NewlineDisplay } from "../../../components/NewlineDisplay";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { Button } from "@material-ui/core";
import { FeatureAddons, FeaturePaymentDeposit } from "../../../app/consts";
import { TF } from "../../../components/typography/TF";
import { EQuoteStatus } from "../../../api/Quote/EQuoteStatus";
import { EEventType, eventAnalytic } from "../../../app/analytics";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../../components/config/ConfigContext";

const QuoteDetailsTable = ({ quote, onRemoveAddon, onInfoDialogOpen, payNextPageLink, isAvailableSchedule }: Props) => {
  const { t } = useTranslation("ab");

  const { isFeatureEnabled } = useConfig();

  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  var minWidth = isSmall ? theme.spacing(4) : "100px";

  if (quote === null) {
    return null;
  }
  const amountPayable = quote.price;
  const vat = quoteVat(quote);

  const itemsView = (items?: QuoteLineItem[]) => {
    if (!items) {
      return null;
    }
    return items
      .slice()
      .sort(lineItemSort)
      .map((li) => (
        <CollapsableRow
          key={li.productId}
          lineItem={li}
          onRemoveAddon={() => {}}
          onInfoDialogOpen={() => {}}
          allowEdits={false}
        />
      ));
  };

  const addonsView = (addons?: QuoteLineItem[], allowEdits: boolean = false) => {
    if (!isFeatureEnabled(FeatureAddons)) {
      return null;
    }

    if (!hasAddons(addons)) {
      return null;
    }

    if (
      addons!.filter((value) => {
        if (value.selected) {
          return true;
        }
        return false;
      }).length === 0
    ) {
      return null;
    }

    const addonRender = addons!
      .slice()
      .sort(lineAddonSort)
      .map((li) => {
        if (!li.selected) {
          return null;
        }
        return (
          <CollapsableRow
            key={li.productId}
            lineItem={li}
            onRemoveAddon={handleClickRemoveButton}
            onInfoDialogOpen={onInfoDialogOpen}
            allowEdits={allowEdits}
          />
        );
      });

    return (
      <>
        <TableRow>
          <TableCell className={classes.detailsCell} colSpan={5}>
            <Box fontWeight="fontWeightBold" padding={1} pl={isSmall ? 2 : 6}>
              Additional items included
            </Box>
          </TableCell>
        </TableRow>
        {addonRender}
      </>
    );
  };

  const deductionsView = (deductions?: QuoteDeduction[]) => {
    if (!deductions) {
      return null;
    }
    return deductions
      .slice()
      .sort(deductionSort)
      .map((d) => <DeductionRow key={d.id} deduction={d} />);
  };

  const handleClickRemoveButton = (addon: QuoteLineItem) => {
    if (onRemoveAddon) {
      onRemoveAddon(addon);
    }
  };

  const outstandingBalance = quoteBalanceOutstanding(quote);
  const paymentsMade: Payment[] = []; // = quotePaymentsMade(quote);

  return (
    <>
      <Table padding="none" className={classes.detailsTable}>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="center" width={minWidth}>
              {isSmall ? "Qty" : "Quantity"}
            </TableCell>
            <Hidden smDown>
              <TableCell align="right" width={minWidth}>
                <Typography noWrap={true} variant="inherit">
                  Per Unit
                </Typography>
              </TableCell>
            </Hidden>
            <TableCell align="right" width={minWidth}>
              {isSmall ? "Amt" : "Amount"}&nbsp;(€)
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemsView(quote.items)}
          {addonsView(quote.addons, allowCustomerEdits(quote))}
          {deductionsView(quote.deductions)}
          {vat > 0 && (
            <TableRow className={classes.resetRow}>
              <TableCell colSpan={isSmall ? 1 : 2} align={isSmall ? "left" : "right"}>
                VAT
              </TableCell>
              <TableCell colSpan={2} align="right">
                <TF format="price">{vat}</TF>
              </TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          )}
          <TableRow className={classes.resetRow}>
            <TableCell colSpan={isSmall ? 1 : 2} align={isSmall ? "left" : "right"}>
              <TF variant="h6" bold>
                Total (inc VAT)
              </TF>
            </TableCell>
            <TableCell colSpan={2} align="right">
              <TF variant="h6" format="price" bold>
                {amountPayable}
              </TF>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {paymentsMade.map((p, i) => (
            <TableRow key={i} className={classes.resetRow}>
              <TableCell colSpan={isSmall ? 1 : 2} align={isSmall ? "left" : "right"}>
                <TF>{p.amount === quote.depositRequirement ? "Deposit Paid" : "Payment"}</TF>
              </TableCell>
              <TableCell colSpan={2} align="right">
                <TF format="price">{p.amount * -1}</TF>
              </TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          ))}
          {paymentsMade.length > 0 && (
            <TableRow className={classes.resetRow}>
              <TableCell colSpan={isSmall ? 1 : 2} align={isSmall ? "left" : "right"}>
                <TF variant="h6" bold>
                  Balance Due
                </TF>
              </TableCell>
              <TableCell colSpan={2} align="right">
                <TF variant="h6" format="price" bold>
                  {outstandingBalance}
                </TF>
              </TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          )}
          <Hidden mdDown>
            {isFeatureEnabled(FeaturePaymentDeposit) && quote.status === EQuoteStatus.Sent && (
              <TableRow className={classes.resetRow}>
                <TableCell colSpan={4} align="right">
                  <Box display="flex" justifyContent="flex-end">
                    <Box width="20%">
                      <Button
                        component={RouterLink}
                        onClick={() => {
                          eventAnalytic.record(EEventType.PayDepositBottom);
                        }}
                        to={payNextPageLink}
                        variant="contained"
                        color="secondary"
                        fullWidth
                      >
                        <TF bold component="span">
                          {isAvailableSchedule
                            ? t("quote.details.next", "View Available Dates")
                            : t("quote.details.next.pay", "Pay €{0} Deposit").replace(
                                "{0}",
                                `${quote.depositRequirement}`
                              )}
                        </TF>
                      </Button>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
            )}
          </Hidden>
        </TableBody>
      </Table>
    </>
  );
};

export default QuoteDetailsTable;

interface CollapsableRowProps {
  lineItem: QuoteLineItem;
  onRemoveAddon: (addon: QuoteLineItem) => void;
  onInfoDialogOpen: (addon: QuoteLineItem) => void;
  allowEdits: boolean;
}

const CollapsableRow = ({ lineItem, onRemoveAddon, onInfoDialogOpen, allowEdits }: CollapsableRowProps) => {
  const classes = useStyles();

  const onRemoveAddonItem = () => {
    if (onRemoveAddon) {
      onRemoveAddon(lineItem);
    }
  };

  const onInfoAddonItem = () => {
    if (onInfoDialogOpen) {
      onInfoDialogOpen(lineItem);
    }
  };

  let info = null;
  let btnDelete = null;
  let quantity = <Box>{lineItem.quantity}</Box>;
  let name = lineItem.description;
  if (lineItem.type === EProductType.Addon) {
    name = lineItem.name;
    info = (
      <Button onClick={onInfoAddonItem}>
        <InfoOutlinedIcon color="secondary" />
      </Button>
    );

    if (allowEdits) {
      btnDelete = (
        <Box alignItems="center">
          <IconButton onClick={onRemoveAddonItem} className={classes.btnDelete} size="small">
            <DeleteOutlineOutlinedIcon color="secondary" />
          </IconButton>
        </Box>
      );
    }
  }

  return (
    <>
      <TableRow>
        <TableCell className={classes.detailsCell}>
          <Box display="flex" alignItems="center">
            <Box>{info}</Box>
            <NewlineDisplay text={name} />
          </Box>
        </TableCell>
        <TableCell className={classes.detailsCell} align="center">
          {quantity}
        </TableCell>
        <Hidden smDown>
          <TableCell className={classes.detailsCell} align="right">
            <TF variant="subtitle1" format="price">
              {lineItem.price}
            </TF>
          </TableCell>
        </Hidden>
        <TableCell className={classes.detailsCell} align="right">
          <TF variant="subtitle1" format="price">
            {lineItem.quantity * lineItem.price}
          </TF>
        </TableCell>
        <TableCell className={classes.detailsCell} align="right">
          {btnDelete}
        </TableCell>
      </TableRow>
    </>
  );
};

const DeductionRow = (props: { deduction: QuoteDeduction }) => {
  const classes = useStyles();
  const { deduction } = props;

  return (
    <>
      <TableRow>
        <TableCell className={classes.detailsCell} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {deduction.description}
        </TableCell>
        <TableCell className={classes.detailsCell} align="right">
          &nbsp;
        </TableCell>
        <Hidden smDown>
          <TableCell className={classes.detailsCell} align="right">
            &nbsp;
          </TableCell>
        </Hidden>
        <TableCell className={classes.detailsCell} align="right">
          <TF format="price">{quoteDeductionValue(deduction)}</TF>
        </TableCell>
        <TableCell className={classes.detailsCell} align="right">
          &nbsp;
        </TableCell>
      </TableRow>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  detailsTable: {
    "& th:not(:first-child)": {
      paddingLeft: "0.5rem"
    },
    "& th": {
      fontWeight: "bold",
      border: 0,
      padding: "0.5rem",
      paddingLeft: theme.spacing(6),
      backgroundColor: colors.alternate,
      [theme.breakpoints.down("md")]: {
        paddingLeft: theme.spacing(2)
      }
    },
    "& td": {
      padding: "0.5rem",
      paddingLeft: theme.spacing(6),
      borderBottom: "none",
      [theme.breakpoints.down("md")]: {
        paddingLeft: theme.spacing(2)
      }
    },
    "& tr:nth-of-type(even)": {
      backgroundColor: colors.alternate
    }
  },
  detailsCell: {
    "borderBottom": "0 !important",
    "padding": "0.5rem 0",
    "verticalAlign": "middle",
    "&:last-child": {
      padding: "0.5rem 0"
    }
  },
  resetRow: {
    backgroundColor: "#FFFFFF !important",
    padding: "0.5rem 0"
  },
  button: {
    width: "90%",
    maxWidth: "17.5rem"
  },
  btnDelete: {
    minWidth: "0px"
  },
  doubleButton: {
    '& [class*="Button-root"]': {
      minWidth: "10rem",
      padding: "6px 8px",
      width: "auto"
    }
  }
}));

interface Props {
  quote: Quote | null;
  onRemoveAddon: (addon: QuoteLineItem) => void;
  onInfoDialogOpen: (addon: QuoteLineItem) => void;
  payNextPageLink: string;
  isAvailableSchedule: boolean;
}
